import React from "react";
import moment from "moment";
import styled from '@emotion/styled';
import {DatePickerInput} from "rc-datepicker";

import Err from "js/common/views/error";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";

const mysqlFormat = "YYYY-MM-DD";

const labelStyle = {
  fontSize: "0.8rem",
  display: "flex",
  alignItems: "center",
  paddingBottom: 2
};

const StyledDatePickerInput = styled.div`
  height: ${props => props.height}px;

  input {
    height: ${props => props.height}px !important;
  }
  
  .react-datepicker-component {
    border: none !important;
  }
  .react-datepicker-component.has-error .react-datepicker-input input {
    border: 2px solid red !important;
  }

  .react-datepicker-component .react-datepicker-input .button-wrapper .input-button {
    padding-right: ${props => (props.height - 36)/2 + 8}px;
    padding-left: ${props => (props.height - 36)/2 + 10}px;
  }

  .react-datepicker-input {
    height: ${props => props.height}px !important;
  }

  .button-wrapper {
    height: ${props => props.height - 4}px !important;

    button {
      height: ${props => props.height - 4}px;
    }
  }
  .react-datepicker-component .react-datepicker-input .button-wrapper {
    position: absolute;
    top: 2px;
    right: 2px;
  }
`;


export default React.memo(({
  style = {},
  onDateChange,
  onErrorChange,
  value,
  placeholder,
  minDate,
  maxDate,
  inlineErrors = false,
  hideError = false,
  hideErrorMessage = false,
  isDisabled = false,
  calendarPosition = "left",
  position = "bottom",
  label,
  className = undefined,
  height,
  setOpenDatePicker
}) => {
  const datePattern = moment.localeData().longDateFormat("L");
  const [strValue, setStrValue] = React.useState(undefined);
  const [error, setError] = React.useState(null);
  const {theme} = React.useContext(CustomThemeContext);
  let input;

  const hasValueAndIsRenderable = React.useMemo(() => {
    if (value) {
      if (isInvalidDate(value)) {
        return !!strValue;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }, [value, strValue]);

  const handleDateChange = (date, dateString) => {
    onDateChange(date, dateString);
    const newError = isInvalidDate(date) ? `Please enter a valid date (format: ${datePattern})` : null;
    if (newError !== error) {
      setError(newError);
      if (onErrorChange) {
        onErrorChange(newError);
      }
    }
  }

  return (
      <div
          className={`picker-${theme.themeId} ${className} ${isDisabled && "disabled"}`}
          style={isDisabled ? {pointerEvents: "none", opacity: 0.7, ...style} : style}
          onClick={e => e.stopPropagation()}>
        {label && <label style={labelStyle}>{label}</label>}
        <StyledDatePickerInput  height={height || 36}>
          <DatePickerInput
              ref={ref => input = ref}
              className={`c19-react-datepicker picker-${theme.themeId} position-datepicker-${calendarPosition} ${!hideError
              &&
              isInvalidDate(value) ? "has-error" : ""}`}
              iconClassName="bhi-calendar"
              position={position}
              style={isInvalidDate(value) ? {border: "1px solid red", borderRadius: 3} : {}}
              displayFormat={datePattern}
              placeholder={placeholder || datePattern}
              minDate={minDate ? minDate.format(mysqlFormat) : null}
              maxDate={maxDate ? maxDate.format(mysqlFormat) : null}
              onShow={() => setOpenDatePicker && setOpenDatePicker(input)}
              onChange={(date, dateStr) => {
                // date, dateStr are both returned by rc-datepicker as "Invalid date" when DatePickerInput is
                // manually cleared by user, hence the use of input.state.dateString as an ugly workaround
                setStrValue(input.state.dateString);
                return handleDateChange(parseDate(dateStr), input.state.dateString);
              }}
              value={hasValueAndIsRenderable ? value.format(mysqlFormat) : null} />
        </StyledDatePickerInput>
        {(!hideError && !hideErrorMessage && isInvalidDate(value))
            && <Err
                text={`Please enter a valid date (format: ${datePattern})`}
                style={inlineErrors ? {position: "absolute", bottom: -45} : {margin: "0.5rem 0"}} />}
      </div>);
});

const parseDate = dateStr => moment(dateStr, mysqlFormat);

const isInvalidDate = date => date && !date.isValid();
